/* MolCellQuest — 地質時代（旅のステージ）定義
   ──────────────────────────────────────────────────────────
   ★ len   : 進化ポイント(evo)での長さ。
             報酬が「現在の evo に対する割合」で伸びるうえ、難易度係数と
             ランク係数が旅の後半ほど大きくなるため、len の比を一定にすると
             後半が一瞬で終わってしまう。そこで len は実際の evo 曲線から
             逆算した値を入れている（どの時代も 20問前後で通過する）。
                 npm run simulate -- --fit 20   ← len を取り直す
                 npm run simulate                ← 通過問題数を確認する
             src/lib/economy.js の報酬定数を変えたら必ず取り直すこと。
   ★ len:0 は最終・無限ステージ（未来）を表す。
   ★ fromMa/toMa : 表示用の地質年代（Ma = 百万年前）。evo とは独立。
             ステージ内の進捗 fraction で線形補間して「◯◯年前」を表示する。
   ★ form  : そのステージでのアバターの姿（data/organism.js の形態キー）。
   ★ checkpoints[].pos : ステージ内の位置（0〜1）。到達で演出が入り、
             旅の風景では道の上の道標（waypoint）として描かれる。
   ────────────────────────────────────────────────────────── */

export const ERAS = [
  {
    id: "hadean",
    name: "冥王代",
    em: "🌋",
    len: 0.31,
    fromMa: 4600,
    toMa: 4000,
    form: "protocell",
    desc: "灼熱のマグマオーシャンが冷え、海と有機分子が生まれた。",
    story: {
      title: "はじまりの惑星",
      lines: [
        "46億年前。生まれたばかりの地球は、溶けた岩の海だった。",
        "降りそそぐ隕石が水をもたらし、やがて雨が降りはじめる。",
        "熱い海の中で、簡単な有機分子が結びつきはじめた。",
        "あなたはまだ、膜に包まれただけの小さな液滴だ。",
      ],
      cta: "旅をはじめる",
    },
    checkpoints: [
      { pos: 0.2, em: "🌕", label: "月の誕生", note: "巨大衝突が地球の自転と潮汐を決めた" },
      { pos: 0.55, em: "🌊", label: "海洋の形成", note: "大気の水蒸気が雨となり、原始の海ができた" },
      { pos: 0.85, em: "🧪", label: "RNAワールド", note: "自己複製するRNAが情報と触媒を兼ねていた" },
    ],
  },
  {
    id: "archean",
    name: "太古代（始生代）",
    em: "🦠",
    len: 0.38,
    fromMa: 4000,
    toMa: 2500,
    form: "bacterium",
    desc: "最初の細胞が生まれ、光合成が世界を変えはじめる。",
    story: {
      title: "最初の細胞",
      lines: [
        "膜・遺伝情報・代謝がひとつにまとまり、ついに「細胞」が誕生した。",
        "熱水噴出孔のまわりで、化学エネルギーを食べる微生物が栄える。",
        "やがて光を使う細菌が現れ、副産物として酸素を吐き出しはじめる。",
        "あなたは今、鞭毛を持つ一匹の原核細胞だ。",
      ],
      cta: "海へ泳ぎ出す",
    },
    checkpoints: [
      { pos: 0.15, em: "🕳️", label: "熱水噴出孔の生態系", note: "化学合成が最初のエネルギー源だった" },
      { pos: 0.42, em: "🧬", label: "共通祖先 LUCA", note: "今の全生物が共有する遺伝暗号の原型" },
      { pos: 0.7, em: "🪨", label: "ストロマトライト", note: "シアノバクテリアの層状構造が浅海を覆う" },
      { pos: 0.92, em: "🫧", label: "酸素発生型光合成", note: "水を分解して酸素を出す反応が広まった" },
    ],
  },
  {
    id: "proterozoic",
    name: "原生代",
    em: "🧫",
    len: 0.81,
    fromMa: 2500,
    toMa: 538.8,
    form: "eukaryote",
    desc: "酸素に満ちた海で、細胞が細胞を飲み込んだ。",
    story: {
      title: "細胞の中の細胞",
      lines: [
        "大気に酸素が満ち、多くの嫌気性生物が姿を消した。",
        "そのとき、ある細胞が別の細胞を飲み込み——消化せずに住まわせた。",
        "ミトコンドリアと葉緑体の誕生。エネルギーの制約が外れる。",
        "核を持つあなたは、もう単純な袋ではない。",
      ],
      cta: "真核細胞になる",
    },
    checkpoints: [
      { pos: 0.1, em: "💨", label: "大酸化イベント", note: "縞状鉄鉱層を残し、大気が酸化的になった" },
      { pos: 0.38, em: "🧬", label: "真核細胞の誕生", note: "細胞内共生でミトコンドリアを獲得" },
      { pos: 0.62, em: "❄️", label: "スノーボールアース", note: "全球凍結を生命は生き延びた" },
      { pos: 0.8, em: "🔀", label: "有性生殖の獲得", note: "減数分裂と組換えが多様性を加速した" },
      { pos: 0.94, em: "🪸", label: "エディアカラ生物群", note: "最初の大型多細胞生物たち" },
    ],
  },
  {
    id: "cambrian",
    name: "カンブリア紀",
    em: "🦐",
    len: 2.2,
    fromMa: 538.8,
    toMa: 485.4,
    form: "arthropod",
    desc: "体の設計図が出そろい、動物のかたちが爆発的に増えた。",
    story: {
      title: "カンブリア爆発",
      lines: [
        "5億3千万年前、動物の門がほぼ出そろう。",
        "眼が生まれ、捕食者と被食者の軍拡競走がはじまった。",
        "Hox遺伝子が体節に「ここは頭、ここは胸」と番地を与える。",
        "あなたは硬い外骨格と複眼を手に入れた。",
      ],
      cta: "海底を歩く",
    },
    checkpoints: [
      { pos: 0.22, em: "👁️", label: "眼の獲得", note: "視覚が生態系の力学を一変させた" },
      { pos: 0.55, em: "🧬", label: "Hox遺伝子と体軸", note: "前後軸に沿って発現し、体の設計図を描く" },
      { pos: 0.85, em: "🦞", label: "バージェス動物群", note: "アノマロカリスたちの奇妙な楽園" },
    ],
  },
  {
    id: "ordovician",
    name: "オルドビス紀",
    em: "🐚",
    len: 5.1,
    fromMa: 485.4,
    toMa: 443.8,
    form: "mollusk",
    desc: "海は生物で満ち、そして最初の緑が陸に上がった。",
    story: {
      title: "陸への最初の一歩",
      lines: [
        "浅い海に大きなサンゴ礁が広がり、オウムガイ類が頂点に立つ。",
        "そのころ、コケのような小さな植物が水際から陸へ這い出した。",
        "だが紀の終わり、氷河期が海の生物の8割以上を奪う。",
        "あなたは殻をまとい、水を噴いて泳いでいる。",
      ],
      cta: "浅海へ",
    },
    checkpoints: [
      { pos: 0.25, em: "🐚", label: "オウムガイ類の繁栄", note: "殻に浮力を蓄える最初の大型捕食者" },
      { pos: 0.58, em: "🌱", label: "植物の上陸", note: "コケ植物の胞子が陸の土壌をつくりはじめた" },
      { pos: 0.9, em: "🧊", label: "オルドビス紀末の大量絶滅", note: "氷河期で海洋生物の約85%が絶滅" },
    ],
  },
  {
    id: "silurian",
    name: "シルル紀（志留紀）",
    em: "🌿",
    len: 12,
    fromMa: 443.8,
    toMa: 419.2,
    form: "fish",
    desc: "維管束が水を運び、顎が獲物を捕らえる。",
    story: {
      title: "顎と維管束",
      lines: [
        "陸上植物が「維管束」を発明し、水を高く汲み上げられるようになった。",
        "海では、鰓弓が変形して「顎」になる——噛む力の誕生だ。",
        "サソリの祖先が陸に上がり、最初の陸上生態系が動きだす。",
        "あなたは背骨と顎を持つ魚になった。",
      ],
      cta: "泳ぎ出す",
    },
    checkpoints: [
      { pos: 0.3, em: "🌿", label: "維管束植物クックソニア", note: "水を運ぶ管が植物を立ち上がらせた" },
      { pos: 0.65, em: "🦂", label: "節足動物の上陸", note: "外骨格が乾燥から体を守った" },
      { pos: 0.9, em: "🦈", label: "顎のある魚の登場", note: "鰓弓の変形が捕食の様式を変えた" },
    ],
  },
  {
    id: "devonian",
    name: "デボン紀",
    em: "🐟",
    len: 64,
    fromMa: 419.2,
    toMa: 358.9,
    form: "tetrapod",
    desc: "魚の時代。そのひれが、やがて四本の足になる。",
    story: {
      title: "ひれから足へ",
      lines: [
        "海と川は魚であふれ、「魚の時代」と呼ばれた。",
        "陸には木が生え、最初の森が空気の組成を変えはじめる。",
        "浅瀬の肉鰭魚のひれの中に、上腕骨・橈骨・尺骨が並んでいた。",
        "あなたは四本の足で、泥の上に体を持ち上げる。",
      ],
      cta: "陸へ上がる",
    },
    checkpoints: [
      { pos: 0.2, em: "🐟", label: "魚の時代", note: "板皮類ダンクルオステウスが海に君臨" },
      { pos: 0.55, em: "🌳", label: "最初の森", note: "アーケオプテリスが根で大地を耕した" },
      { pos: 0.86, em: "🦎", label: "四肢動物の出現", note: "ティクターリクのひれには腕の骨があった" },
    ],
  },
  {
    id: "carboniferous",
    name: "石炭紀",
    em: "🌲",
    len: 300,
    fromMa: 358.9,
    toMa: 298.9,
    form: "amphibian",
    desc: "巨大なシダの森。高い酸素濃度が、巨大な虫を生んだ。",
    story: {
      title: "巨大シダの森",
      lines: [
        "リンボクとロボクの森が大陸を覆い、のちの石炭になる。",
        "大気の酸素は35%に達し、翼開長70cmのトンボが飛んだ。",
        "そして殻と羊膜を持つ卵が生まれ、卵は水を離れた。",
        "あなたは湿った森で、皮膚呼吸をしながら暮らしている。",
      ],
      cta: "森へ",
    },
    checkpoints: [
      { pos: 0.25, em: "🌲", label: "巨大シダの大森林", note: "分解されない材が石炭として積もった" },
      { pos: 0.58, em: "🥚", label: "有羊膜卵の獲得", note: "卵の中に「小さな池」を持ち込んだ" },
      { pos: 0.86, em: "🦗", label: "高酸素と巨大節足動物", note: "気管呼吸の限界が押し広げられた" },
    ],
  },
  {
    id: "permian",
    name: "ペルム紀（二畳紀）",
    em: "🏜️",
    len: 1500,
    fromMa: 298.9,
    toMa: 251.9,
    form: "synapsid",
    desc: "ひとつの超大陸。そして史上最大の絶滅。",
    story: {
      title: "パンゲアと大絶滅",
      lines: [
        "すべての大陸がひとつに集まり、内陸は乾いた砂漠になった。",
        "背に帆を持つ単弓類——哺乳類の祖先——が繁栄する。",
        "だが2億5190万年前、シベリアの巨大噴火が海と空を毒した。",
        "生物種の9割が消えた。あなたは、生き延びた1割の側にいる。",
      ],
      cta: "生き延びる",
    },
    checkpoints: [
      { pos: 0.25, em: "🗺️", label: "超大陸パンゲア", note: "海岸線が減り、内陸は極度に乾燥した" },
      { pos: 0.6, em: "🦴", label: "単弓類の繁栄", note: "側頭窓をひとつ持つ、哺乳類につながる系統" },
      { pos: 0.94, em: "☠️", label: "P–T境界 大量絶滅", note: "海洋種の約96%が失われた史上最大の絶滅" },
    ],
  },
  {
    id: "triassic",
    name: "三畳紀",
    em: "🦎",
    len: 7800,
    fromMa: 251.9,
    toMa: 201.4,
    form: "reptile",
    desc: "空いた生態系の椅子に、恐竜と哺乳形類が座った。",
    story: {
      title: "空席だらけの世界",
      lines: [
        "大絶滅のあと、生態系にはたくさんの空席があった。",
        "主竜類から恐竜が現れ、後ろ足で立って走りはじめる。",
        "同じころ、小さな夜行性の哺乳形類がひっそりと生まれた。",
        "あなたは鱗に覆われ、乾いた大地を歩いている。",
      ],
      cta: "新しい世界へ",
    },
    checkpoints: [
      { pos: 0.25, em: "🌱", label: "生態系の回復", note: "回復には約1000万年を要した" },
      { pos: 0.58, em: "🦖", label: "恐竜の出現", note: "直立歩行が高速な移動を可能にした" },
      { pos: 0.87, em: "🐭", label: "哺乳形類の登場", note: "小型・夜行性・高代謝という戦略" },
    ],
  },
  {
    id: "jurassic",
    name: "ジュラ紀",
    em: "🦕",
    len: 39000,
    fromMa: 201.4,
    toMa: 145.0,
    form: "dinosaur",
    desc: "巨大竜脚類の足音と、羽毛をまとった最初の翼。",
    story: {
      title: "巨大化と羽毛",
      lines: [
        "気嚢を備えた呼吸器が、竜脚類を30メートル級まで押し上げた。",
        "シダとソテツの森を、長い首が薙ぐように渡っていく。",
        "そして獣脚類の一部が、断熱のための羽毛を飛翔に転用した。",
        "あなたの体は大きく、鼓動は力強い。",
      ],
      cta: "大地を踏む",
    },
    checkpoints: [
      { pos: 0.3, em: "🦕", label: "竜脚類の巨大化", note: "気嚢と中空の骨が巨体を支えた" },
      { pos: 0.66, em: "🪶", label: "始祖鳥と羽毛", note: "羽毛は保温から飛翔へ転用された" },
      { pos: 0.9, em: "🌊", label: "アンモナイトの海", note: "浅い内海に無数の巻き貝が漂う" },
    ],
  },
  {
    id: "cretaceous",
    name: "白亜紀",
    em: "🦖",
    len: 200000,
    fromMa: 145.0,
    toMa: 66.0,
    form: "bird",
    desc: "花が咲き、頂点捕食者が君臨し、そして空から石が落ちた。",
    story: {
      title: "花と衝突",
      lines: [
        "被子植物が現れ、昆虫と共進化して世界を色で埋めた。",
        "ティラノサウルスが君臨し、哺乳類は足元で多様化していく。",
        "6600万年前、直径10kmの小惑星がユカタン半島に落ちる。",
        "空を飛べたあなたの系統だけが、恐竜として生き残った。",
      ],
      cta: "空へ逃れる",
    },
    checkpoints: [
      { pos: 0.25, em: "🌸", label: "被子植物の登場", note: "花と昆虫の共進化がはじまった" },
      { pos: 0.55, em: "🐁", label: "真獣類の分化", note: "胎盤を持つ哺乳類の系統が分かれた" },
      { pos: 0.8, em: "🦖", label: "ティラノサウルスの時代", note: "白亜紀末の北米に君臨した頂点捕食者" },
      { pos: 0.97, em: "☄️", label: "K–Pg境界の衝突", note: "イリジウム異常が語る、一日で変わった世界" },
    ],
  },
  {
    id: "paleogene",
    name: "古第三紀",
    em: "🌳",
    len: 3200000,
    fromMa: 66.0,
    toMa: 23.03,
    form: "mammal",
    desc: "哺乳類が空席を埋め、体を大きくしていく。",
    story: {
      title: "哺乳類の時代",
      lines: [
        "恐竜が去った世界で、哺乳類は一気に多様化した。",
        "森を走るもの、木に登るもの、海へ帰るもの。",
        "暁新世・始新世・漸新世と、気候は暖まりまた冷えていく。",
        "あなたは毛皮と一定の体温を持つ、恒温の獣だ。",
      ],
      cta: "森を駆ける",
    },
    checkpoints: [
      { pos: 0.15, em: "🌤️", label: "暁新世", note: "小型哺乳類の適応放散がはじまる" },
      { pos: 0.5, em: "🌴", label: "始新世", note: "温暖化のピーク。クジラが海へ帰った" },
      { pos: 0.85, em: "🌾", label: "漸新世", note: "寒冷化が進み、草原が広がりはじめる" },
    ],
  },
  {
    id: "neogene",
    name: "新第三紀",
    em: "🐒",
    len: 75000000,
    fromMa: 23.03,
    toMa: 2.58,
    form: "primate",
    desc: "草原が広がり、類人猿が立ち上がる。",
    story: {
      title: "草原に立つ",
      lines: [
        "C4植物の草原が大陸を覆い、草食獣の歯が高く進化した。",
        "アフリカの森が切れぎれになり、地上を歩く類人猿が現れる。",
        "骨盤と足のかたちが変わり、視線が高くなった。",
        "あなたは二本の足で立ち、両手が自由になった。",
      ],
      cta: "立ち上がる",
    },
    checkpoints: [
      { pos: 0.35, em: "🌾", label: "中新世", note: "草原の拡大と類人猿の多様化" },
      { pos: 0.82, em: "🚶", label: "鮮新世", note: "直立二足歩行の確立。アウストラロピテクス" },
    ],
  },
  {
    id: "quaternary",
    name: "第四紀",
    em: "🧑",
    len: 1700000000,
    fromMa: 2.58,
    toMa: 0,
    form: "human",
    desc: "氷期をくぐり抜け、言葉と農耕が世界を変えた。",
    story: {
      title: "氷と火",
      lines: [
        "氷期と間氷期が10万年周期でくり返す、揺れ動く気候。",
        "火を使い、道具を作り、物語を語る種がアフリカを出た。",
        "1万年前、農耕がはじまり、人は自分で生態系を作りはじめる。",
        "あなたは今、この文章を読んでいる。",
      ],
      cta: "現在へ",
    },
    checkpoints: [
      { pos: 0.4, em: "🧊", label: "更新世", note: "氷期と間氷期のくり返し。マンモスの時代" },
      { pos: 0.78, em: "🔥", label: "ホモ・サピエンスの拡散", note: "言語と象徴的思考が広域の協力を可能にした" },
      { pos: 0.97, em: "🌾", label: "完新世", note: "農耕と定住、そして文明のはじまり" },
    ],
  },
  {
    id: "future",
    name: "人新世、そしてその先へ",
    em: "🧬",
    len: 0, // 無限ステージ
    fromMa: 0,
    toMa: null,
    form: "posthuman",
    desc: "進化はまだ終わらない。次に何を設計する？",
    story: {
      title: "設計される進化",
      lines: [
        "はじめて、進化を読み書きできる生物が現れた。",
        "ゲノム編集、合成生物学、人工細胞——道具はもう手の中にある。",
        "46億年かけて書かれた文章に、あなたが続きを書く番だ。",
        "旅はここから、果てしなく続いていく。",
      ],
      cta: "未来へ",
    },
    checkpoints: [], // ラップごとに自動生成される
  },
];

/** 無限ステージ1周ぶんの evo。ざっくり「100万年進む」体感に対応させる。 */
export const FUTURE_LAP = 4.3e9;

/** 未来ステージ 1ラップあたり何百万年進んだことにするか（表示用） */
export const FUTURE_LAP_MA = 1.0;

// 累積 evo（startEvo / endEvo）をその場で計算して各時代に持たせる
let acc = 0;
for (const era of ERAS) {
  era.startEvo = acc;
  if (era.len > 0) {
    acc += era.len;
    era.endEvo = acc;
  } else {
    era.endEvo = Infinity;
  }
}

/** 全時代の合計 evo（未来ステージを除く） */
export const TOTAL_FINITE_EVO = acc;

/** id → 時代 */
export const ERA_BY_ID = Object.fromEntries(ERAS.map((e) => [e.id, e]));
